import {
  ChakraFlex,
  FAIconName,
  InputGroupV2,
  PhoneNumbersV2,
  TextInputV2,
  UniversalSelect,
} from '@equip.health/ui';

import { validateEmail } from '~/lib/common';
import { DATE_PLACEHOLDER } from '~/lib/constants';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import { EDITBASICINFO_PHONES_REQUIRED_FIELDS } from '~/lib/constants/patients.constants';
import { isValidDateOfBirth } from '~/lib/util/patient.util';
import {
  ERROR_MESSAGES,
  getMultiSelectedUniversalSelectOptions,
  isCharacterCountExceeded,
} from '~/lib/util/profile.util';
import { useGetContactPreferencesEnumQuery } from '~/store/services/enumerations';

import { validateContactPhonesField } from '~/lib/util/patientContact.util';
import { toMultiSelectOption } from '~/lib/util/menu';
import { PhoneFieldValue } from '~/lib/types/patient/PhoneFieldValue';

interface ProfilePersonalInfoProps {
  appendSurveyResult: any;
  personalInfo: ProfilePersonalInfoData;
  phonesData: PhonesFieldType;
  hasEquipApp?: boolean;
  basicInfoEmail?: string;
  updatePersonalInfo: (
    field: keyof ProfilePersonalInfoData | 'phonesField',
    value: string | string[] | boolean | PhonesFieldType,
    isInvalid?: boolean,
  ) => void;
  updateSection: (section: 'phonesField', updatedData: PhonesFieldType) => void;
}

const ProfilePersonalInfo = ({
  appendSurveyResult,
  hasEquipApp,
  personalInfo,
  phonesData,
  basicInfoEmail,
  updatePersonalInfo,
  updateSection,
}: ProfilePersonalInfoProps) => {
  const TOP_LEVEL_ID = 'patient-profile-personal-info-form';

  const {
    data: contactPreferences,
    isLoading: isGetContactPreferencesLoading,
  } = useGetContactPreferencesEnumQuery();

  const validateField = (
    field: keyof ProfilePersonalInfoData | 'phonesField',
    value: string,
  ): boolean => {
    switch (field) {
      case 'firstName':
      case 'chosenName':
      case 'lastName':
        return !isCharacterCountExceeded(value, 75);
      case 'email':
        return !value || validateEmail(value);
      case 'mrn':
        return !isCharacterCountExceeded(value, 65);
      case 'dateOfBirth':
        return !value || isValidDateOfBirth(value);

      default:
        return true;
    }
  };

  const handleFieldChange = (
    field: keyof ProfilePersonalInfoData,
    value: string | null,
  ) => {
    const isFieldValid = validateField(field, value);
    if (field === 'email' && personalInfo.isDuplicateEmail) {
      updatePersonalInfo('isDuplicateEmail', false);
    }
    updatePersonalInfo(field, value, !isFieldValid);
  };

  const handlePhonesChange = (phoneValue: PhoneFieldValue) => {
    const completePhonesData: PhonesFieldType = {
      ...phoneValue,
      mobile: phoneValue.mobile ?? (phonesData.mobile || null),
    };
    const isPhonesFieldValid = validateContactPhonesField(
      completePhonesData,
      hasEquipApp,
    );
    updatePersonalInfo('phonesField', completePhonesData, !isPhonesFieldValid);
    updateSection('phonesField', { ...phonesData, ...phoneValue });
  };
  const shouldHighlightField = (
    fieldName: keyof ProfilePersonalInfoData,
  ): boolean => {
    return appendSurveyResult.hasOwnProperty(fieldName);
  };
  const contactPreferencesOptions =
    contactPreferences?.map(toMultiSelectOption) || [];

  return (
    <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__first-name`}
        label="First name"
        shouldShowAsteriskWhenRequired
        value={
          personalInfo.firstName &&
          personalInfo.invalidFields.firstName &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__first-name-input`}
          isInvalid={personalInfo.invalidFields.firstName}
          onChange={(value) => handleFieldChange('firstName', value)}
          value={personalInfo.firstName}
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__last-name`}
        label="Last name"
        shouldShowAsteriskWhenRequired
        value={
          personalInfo.lastName &&
          personalInfo.invalidFields.lastName &&
          ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__last-name-input`}
          isInvalid={personalInfo.invalidFields.lastName}
          onChange={(value) => handleFieldChange('lastName', value)}
          value={personalInfo.lastName}
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__chosen-name`}
        isHighlight={shouldHighlightField('chosenName')}
        label="Chosen name"
        value={
          personalInfo.invalidFields.chosenName && ERROR_MESSAGES.NAME_LIMIT
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__chosen-name-input`}
          isHighlight={shouldHighlightField('chosenName')}
          isInvalid={personalInfo.invalidFields.chosenName}
          onChange={(value) => handleFieldChange('chosenName', value)}
          value={personalInfo.chosenName}
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__date-of-birth`}
        label="Date of birth"
        shouldShowAsteriskWhenRequired
        value={
          personalInfo.dateOfBirth &&
          personalInfo.invalidFields.dateOfBirth &&
          'Please enter valid date of birth'
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__date-of-birth-input`}
          isInvalid={personalInfo.invalidFields.dateOfBirth}
          leftIcon={FAIconName.calendarDayRegular}
          onChange={(value) => handleFieldChange('dateOfBirth', value)}
          placeholder={DATE_PLACEHOLDER}
          type={TextInputV2Type.DATE}
          value={personalInfo.dateOfBirth}
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__mrn`}
        isHighlight={shouldHighlightField('mrn')}
        label="MRN"
        value={personalInfo.invalidFields.mrn && ERROR_MESSAGES.MRN_LIMIT}
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__mrn-input`}
          isHighlight={shouldHighlightField('mrn')}
          isInvalid={personalInfo.invalidFields.mrn}
          isReadOnly
          onChange={(value) => handleFieldChange('mrn', value)}
          value={personalInfo.mrn}
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__email`}
        isHighlight={shouldHighlightField('email')}
        label="Email address"
        shouldShowAsteriskWhenRequired={basicInfoEmail ? true : false}
        value={
          (personalInfo.email &&
            personalInfo.invalidFields.email &&
            'Please enter a valid email') ||
          (personalInfo.isDuplicateEmail && 'Duplicate email found')
        }
      >
        <TextInputV2
          id={`${TOP_LEVEL_ID}__email-input`}
          isHighlight={shouldHighlightField('email')}
          isInvalid={
            personalInfo.invalidFields.email || personalInfo.isDuplicateEmail
          }
          onChange={(value) => handleFieldChange('email', value)}
          value={personalInfo.email}
        />
      </InputGroupV2>
      <PhoneNumbersV2
        id={`${TOP_LEVEL_ID}__phone-input`}
        mobileRequired={hasEquipApp}
        phonesData={phonesData}
        requiredFields={EDITBASICINFO_PHONES_REQUIRED_FIELDS}
        updatePhonesField={(pfv: PhoneFieldValue) => {
          handlePhonesChange(pfv);
        }}
      />
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__preferred-contact-method`}
        isHighlight={shouldHighlightField('contactPreferences')}
        label="Preferred contact method"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__preferred-contact-method-select`}
          isHighlight={shouldHighlightField('contactPreferences')}
          isLoading={isGetContactPreferencesLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('contactPreferences', selectedValues);
          }}
          options={contactPreferencesOptions}
          value={getMultiSelectedUniversalSelectOptions(
            personalInfo.contactPreferences,
            contactPreferencesOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>
    </ChakraFlex>
  );
};

export default ProfilePersonalInfo;
